import React, { memo } from 'react';
import { Form } from 'react-bootstrap';
import {
  MSP_INDICADORES_COLUMNAS,
  normalizeIndicadorColValue,
  indicadorRowLevelClass,
} from '../../data/indicadoresSaludBucalCols';
import { preserveScrollWhile } from '../../utils/preserveScrollPosition';
import MspSelectionBox from './MspSelectionBox';

/**
 * Tres columnas MSP: enfermedad periodontal, mal oclusión (Angle), fluorosis.
 * Una opción activa por columna (casilla con ×).
 */
export default memo(function IndicadoresSaludBucalCols({
  enfermedadPeriodontal = '',
  angleClasificacion = '',
  fluorosis = '',
  maloclusion = '',
  onChange,
  readOnly = false,
  showMaloclusionNotas = true,
  showSectionTitle = true,
}) {
  const values = {
    enfermedadPeriodontal: normalizeIndicadorColValue('enfermedadPeriodontal', enfermedadPeriodontal),
    angleClasificacion: normalizeIndicadorColValue('angleClasificacion', angleClasificacion),
    fluorosis: normalizeIndicadorColValue('fluorosis', fluorosis),
  };

  const setField = (field, next, anchorEl) => {
    if (!onChange) return;
    const apply = () => onChange({ [field]: next });
    if (anchorEl) {
      preserveScrollWhile(anchorEl, apply);
    } else {
      apply();
    }
  };

  const toggleRow = (field, rowValue, anchorEl) => {
    const current = values[field];
    setField(field, current === rowValue ? '' : rowValue, anchorEl);
  };

  if (readOnly) {
    const seleccionados = MSP_INDICADORES_COLUMNAS.map((col) => {
      const val = values[col.field];
      const row = col.rows.find((r) => r.value === val);
      return { col, row, val };
    }).filter((x) => x.val);

    return (
      <div className="msp-ind-panel msp-ind-panel--readonly">
        {showSectionTitle && (
          <div className="msp-ind-panel__header">Enfermedad periodontal · Mal oclusión · Fluorosis</div>
        )}
        {seleccionados.length === 0 ? (
          <p className="msp-read-ind-empty">Sin clasificación registrada en estas columnas.</p>
        ) : (
          <div className="msp-read-ind-cards">
            {seleccionados.map(({ col, row }) => (
              <div
                key={col.field}
                className={`msp-read-ind-card msp-read-ind-card--${col.theme}`}
              >
                <span className="msp-read-ind-card__lbl">
                  <i className={`bi ${col.icon}`} aria-hidden />
                  {col.title}
                </span>
                <span className="msp-read-ind-card__val">{row?.label || '—'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="msp-ind-panel">
      {showSectionTitle && (
        <div className="msp-ind-panel__header">
          <i className="bi bi-clipboard2-pulse" aria-hidden />
          Enfermedad periodontal · Mal oclusión · Fluorosis
        </div>
      )}

      <div className="msp-ind-help" role="note">
        <i className="bi bi-hand-index-thumb" aria-hidden />
        <p>
          Pulse la <strong>fila</strong> o el cuadro junto a la opción; aparecerá <strong>×</strong> (una sola por
          columna). Vuelva a pulsar para desmarcar.
        </p>
      </div>

      <div className="msp-ind-cols" role="group" aria-label="Indicadores de salud bucal">
        {MSP_INDICADORES_COLUMNAS.map((col) => (
          <div
            key={col.field}
            className={`msp-ind-col msp-ind-col--${col.theme}`}
            role="group"
            aria-label={col.title}
          >
            <div className="msp-ind-col__title">
              <i className={`bi ${col.icon}`} aria-hidden />
              <span>{col.title}</span>
            </div>
            <div className="msp-ind-col__body">
              {col.rows.map((row) => {
                const selected = values[col.field] === row.value;
                const rowClass = [
                  'msp-ind-row',
                  indicadorRowLevelClass(row.value),
                  selected ? 'msp-ind-row--selected' : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                return (
                  <button
                    key={row.value}
                    type="button"
                    className={rowClass}
                    aria-pressed={selected}
                    aria-label={`${col.title} — ${row.label}`}
                    onClick={(e) => toggleRow(col.field, row.value, e.currentTarget)}
                  >
                    <span className="msp-ind-label">
                      <span className="msp-ind-label__text">{row.label}</span>
                    </span>
                    <span className="msp-ind-row__sel" aria-hidden>
                      <MspSelectionBox variant="inline" selected={selected} readOnly />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {showMaloclusionNotas && (
        <div className="msp-ind-notas">
          {readOnly ? (
            maloclusion ? (
              <p className="small text-muted mb-0">
                <span className="fw-semibold">Notas maloclusión:</span> {maloclusion}
              </p>
            ) : null
          ) : (
            <Form.Group className="mb-0">
              <Form.Label className="msp-ind-notas__label">
                Notas adicionales maloclusión (opcional)
              </Form.Label>
              <Form.Control
                size="sm"
                className="msp-ind-notas__input"
                value={maloclusion || ''}
                onChange={(e) =>
                  preserveScrollWhile(e.target, () => onChange?.({ maloclusion: e.target.value }))
                }
                placeholder="Ej. apiñamiento, resalte, mordida abierta…"
              />
            </Form.Group>
          )}
        </div>
      )}

      {!readOnly && (
        <p className="msp-ind-leyenda small text-muted mb-0">
          Una × por columna · colores indican gravedad o clase Angle.
        </p>
      )}
    </div>
  );
});
